import React, { PropsWithChildren, ReactNode } from "react"
import Box from "@material-ui/core/Box"
import { makeStyles, Theme } from "@material-ui/core/styles"
import { Palette } from "@material-ui/core/styles/createPalette"

// ---- utils ----
export const isColorProp = (strColor: string | null) => {
    if (!strColor) return false
    const s = new Option().style
    s.color = strColor
    return s.color !== ""
}

const getBackgroundColor = (color: string, palette: Palette): string => {
    if (Object.prototype.hasOwnProperty.call(palette.background, color)) {
        const bgs = palette.background as unknown as Record<string, string>
        return bgs[color] ?? "#fff"
    }
    return (isColorProp(color) && color) || "#fff"
}

// ---- consts ----
export const RADIUS = {
    SMALL: 5,
    BIG: 20,
    MEDIUM: 10,
} as const

// ---- styles ----
type WrapperProps = {
    fontSize?: number
    color?: string
    animation?: string
    infinite?: boolean
}

const useTagStyles = makeStyles<Theme, WrapperProps>((theme) => ({
    wrapper: (props) => {
        const bg = getBackgroundColor(props.color || "#fff", theme.palette)
        return {
            backgroundColor: bg,
            color: theme.palette.getContrastText(bg || "#fff"),
            padding: `${theme.spacing(0.5)}px ${theme.spacing(1)}px`,
            border: "solid 1px transparent",
            borderRadius: RADIUS.SMALL,
            ...theme.typography.caption,
            fontSize: props.fontSize ?? "inherit",
            fontWeight: "bold",
            ...(props.animation
                ? {
                      boxShadow: "0 0 0 0 rgba(0, 0, 0, 1)",
                      animation: `${props.animation} 1.5s ${props.infinite ? "infinite" : ""}`,
                  }
                : undefined),
        }
    },
}))

interface TagProps extends WrapperProps {
    children: ReactNode
}

export const Tag = ({ infinite, animation, color, children, fontSize }: TagProps) => {
    const classes = useTagStyles({ infinite, animation, color, fontSize })
    return <span className={classes["wrapper"]}>{children}</span>
}

// ---- rounded tag ----
type TagRoundedSize = "sm" | "default"

type RoundedStyleProps = {
    size: TagRoundedSize
}

const useRoundedStyles = makeStyles<Theme, RoundedStyleProps>((theme: Theme) => ({
    container: (props) => {
        const dimension = props.size === "sm" ? "3.125rem" : "5rem"
        const padding = props.size === "sm" ? "0.5rem" : "0.375rem 0.75rem"

        return {
            padding,
            width: dimension,
            height: dimension,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            wordWrap: "break-word",
            borderRadius: "50%",
            textAlign: "center",
            fontFamily: theme.typography.fontFamily,
            backgroundColor: theme.palette.common.black,
            color: theme.palette.common.white,
        }
    },
}))

interface TagRoundedProps {
    size?: TagRoundedSize
}

export const TagRounded = ({ children, size = "default" }: PropsWithChildren<TagRoundedProps>) => {
    const classes = useRoundedStyles({ size })
    return <Box className={classes["container"]}>{children}</Box>
}
